import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useBlocker } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { formatCurrency } from '../utils/formatters';
import OrderModal from '../components/OrderModal';
import ClientSelectorModal from '../components/ClientSelectorModal';
import { CLIENT_LABEL_MAPPING } from '../utils/clientLabels';
import './CadastroClientes.css';

const CadastroClientes = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [clienteAtual, setClienteAtual] = useState(0);
  const [criterios, setCriterios] = useState({ Nome: '', ID: '', EnderecoCobranca: '' });
  const [clienteEditado, setClienteEditado] = useState(null);
  const [pedidos, setPedidos] = useState([]);
  const [loadingPedidos, setLoadingPedidos] = useState(false);
  const [isOrderModalOpen, setIsOrderModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalCliente, setOriginalCliente] = useState(null);
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  const [isClientSelectorOpen, setIsClientSelectorOpen] = useState(false);
  const [searchBy, setSearchBy] = useState({ Nome: true, ID: false, EnderecoCobranca: false });


  // Bloqueador de navegação (React Router)
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
  );

  // Efeito para lidar com o blocker do React Router
  useEffect(() => {
    if (blocker.state === "blocked") {
      const proceed = window.confirm("Você tem alterações não salvas. Deseja sair sem salvar?");
      if (proceed) {
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [blocker]);

  // Buscar pedidos do cliente atual
  const buscarPedidos = useCallback(async () => {
    const idCliente = clientes[clienteAtual]?.ID;
    if (!idCliente) {
      setPedidos([]);
      return;
    }

    setLoadingPedidos(true);
    try {
      const response = await fetch(`/api/clientes/${idCliente}/pedidos`, { credentials: 'include' });
      const data = await response.json();

      if (Array.isArray(data)) {
        setPedidos(data);
      } else {
        console.error('Resposta inesperada da API:', data);
        setPedidos([]);
      }
    } catch (error) {
      console.error('Erro ao buscar pedidos:', error);
      setPedidos([]);
    } finally {
      setLoadingPedidos(false);
    }
  }, [clientes, clienteAtual]);

  // Buscar informações do usuário e clientes
  useEffect(() => {
    const carregarDados = async () => {
      try {
        // Fetch User
        const userRes = await fetch('/api/auth/me', { credentials: 'include' });
        if (userRes.ok) {
          const userData = await userRes.json();
          setUser(userData.user);
        } else {
          navigate('/login');
          return;
        }

        const response = await fetch('/api/clientes', { credentials: 'include' });
        const data = await response.json();

        // Buscar o último cliente visitado ANTES de disparar efeitos colaterais
        const lastVisitedRes = await fetch('/api/ultimo-visitado', { credentials: 'include' });
        const lastVisitedData = await lastVisitedRes.json();

        setClientes(data); // Isso agora será batched com o setClienteAtual abaixo (React 18)

        if (lastVisitedData.clientId && data.length > 0) {
          const index = data.findIndex(c => String(c.ID) === String(lastVisitedData.clientId));
          if (index !== -1) {
            setClienteAtual(index);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      }
    };
    carregarDados();
  }, [navigate]);

  // Atualizar clienteEditado e salvar último visitado quando clienteAtual mudar
  useEffect(() => {
    const cliente = clientes[clienteAtual];
    setClienteEditado(cliente || null);
    setOriginalCliente(cliente ? JSON.parse(JSON.stringify(cliente)) : null);
    setHasUnsavedChanges(false);

    if (cliente) {
      buscarPedidos(); // Buscar automaticamente ao trocar de cliente
      fetch('/api/salvar-ultimo-visitado', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId: cliente.ID }),
        credentials: 'include'
      }).catch(err => console.error('Erro ao salvar último visitado:', err));
    } else {
      setPedidos([]);
    }
  }, [clienteAtual, clientes, buscarPedidos]);

  const salvarAlteracoesAuto = useCallback(async () => {
    try {
      const response = await fetch(`/api/clientes/${clienteEditado.ID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clienteEditado),
      });
      if (response.ok) {
        console.log('✅ Auto-save: Alterações salvas automaticamente');
        const clientesAtualizados = [...clientes];
        clientesAtualizados[clienteAtual] = clienteEditado;
        setClientes(clientesAtualizados);
        setOriginalCliente(JSON.parse(JSON.stringify(clienteEditado)));
        setHasUnsavedChanges(false);
      }
    } catch (error) {
      console.error('Erro no auto-save:', error);
    }
  }, [clienteEditado, clientes, clienteAtual]);

  // Detectar mudanças não salvas
  useEffect(() => {
    if (!clienteEditado || !originalCliente) {
      setHasUnsavedChanges(false);
      return;
    }

    const hasChanges = JSON.stringify(clienteEditado) !== JSON.stringify(originalCliente);
    setHasUnsavedChanges(hasChanges);

    // Limpar timer anterior
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }

    // Se houver mudanças, iniciar timer de auto-save (1 minuto)
    if (hasChanges) {
      const timer = setTimeout(() => {
        salvarAlteracoesAuto();
      }, 60000); // 60 segundos
      setAutoSaveTimer(timer);
    }

    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
  }, [clienteEditado, originalCliente, autoSaveTimer, salvarAlteracoesAuto]);

  const salvarAlteracoes = async () => {
    try {
      const response = await fetch(`/api/clientes/${clienteEditado.ID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(clienteEditado),
      });
      if (response.ok) {
        alert('Alterações salvas com sucesso!');
        const clientesAtualizados = [...clientes];
        clientesAtualizados[clienteAtual] = clienteEditado;
        setClientes(clientesAtualizados);
        setOriginalCliente(JSON.parse(JSON.stringify(clienteEditado)));
        setHasUnsavedChanges(false);
      } else {
        alert('Erro ao salvar alterações.');
      }
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
    }
  };

  const buscarClientePorCriterios = async () => {
    try {
      const filteredCriterios = Object.fromEntries(
        Object.entries(criterios).filter(([key, v]) => v !== '' && (key === 'global' || searchBy[key]))
      );

      let query;
      if (criterios.global) {
        // Advanced search with checkboxes
        const activeFields = Object.keys(searchBy).filter(k => searchBy[k]);
        const fieldsQuery = activeFields.map(f => `fields=${f}`).join('&');
        query = `search=${encodeURIComponent(criterios.global)}&${fieldsQuery}`;
      } else {
        query = new URLSearchParams(filteredCriterios).toString();
      }

      const response = await fetch(`/api/clientes?${query}`, { credentials: 'include' });
      const data = await response.json();
      setClientes(data);
      setClienteAtual(0);
    } catch (error) {
      console.error('Erro na pesquisa de clientes:', error);
    }
  };

  const criarNovoCliente = async () => {
    try {
      const response = await fetch('/api/clientes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          SobrenomeContato: 'Novo',
          PrimeiroNomeContato: 'Cliente'
        }),
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        // Recarregar a lista de clientes para incluir o novo
        const resList = await fetch('/api/clientes', { credentials: 'include' });
        const dataList = await resList.json();
        setClientes(dataList);

        // Encontrar o index do novo cliente
        const index = dataList.findIndex(c => String(c.ID) === String(data.ID));
        if (index !== -1) {
          setClienteAtual(index);
        }
        alert('Novo cliente criado com sucesso! Preencha os detalhes desejados.');
      } else {
        alert('Erro ao criar novo cliente.');
      }
    } catch (error) {
      console.error('Erro ao criar cliente:', error);
    }
  };

  const checkUnsavedChanges = () => {
    if (hasUnsavedChanges) {
      return window.confirm('Você tem alterações não salvas. Deseja sair sem salvar?');
    }
    return true;
  };

  const openOrderModal = (order = null) => {
    setSelectedOrder(order);
    setIsOrderModalOpen(true);
  };

  return (
    <div className="page-container">
      <Navbar user={user} />
      <header className="page-header">
        <h2>
          Cadastro de Clientes
          {hasUnsavedChanges && <span style={{ color: 'red', fontSize: '0.9rem', marginLeft: '15px' }}>● Alterações não salvas</span>}
        </h2>
        <div className="search-bar">
          <div className="search-controls">
            <div className="search-criteria">
              <label><input type="radio" name="searchCriteria" checked={searchBy.Nome} onChange={() => setSearchBy({ Nome: true, ID: false, EnderecoCobranca: false })} /> {CLIENT_LABEL_MAPPING['SobrenomeContato']}</label>
              <label><input type="radio" name="searchCriteria" checked={searchBy.ID} onChange={() => setSearchBy({ Nome: false, ID: true, EnderecoCobranca: false })} /> Código</label>
              <label><input type="radio" name="searchCriteria" checked={searchBy.EnderecoCobranca} onChange={() => setSearchBy({ Nome: false, ID: false, EnderecoCobranca: true })} /> {CLIENT_LABEL_MAPPING['EnderecoCobranca']}</label>
            </div>
            <div className="search-input-group">
              <input
                type="text"
                placeholder="Pesquisar clientes..."
                value={criterios.global || ''}
                onChange={(e) => setCriterios({ ...criterios, global: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && buscarClientePorCriterios()}
              />
              <button className="btn-search" onClick={buscarClientePorCriterios}>Pesquisar</button>
              <button className="btn-classic" onClick={() => setIsClientSelectorOpen(true)}>Procurar...</button>
            </div>
          </div>
          <div className="search-actions">
            <button className="btn-clear" onClick={() => { setCriterios({}); setSearchBy({ Nome: true, ID: false, EnderecoCobranca: false }); }}>Limpar</button>
            <button className="btn-add-client" onClick={criarNovoCliente}>+ Novo Cliente</button>
          </div>
        </div>
      </header>

      <div className="content">
        <main className="main-content">
          {clienteEditado ? (
            <div className="cliente-detalhes">
              <h2>Detalhes do Cliente</h2>
              {Object.keys(clienteEditado)
                .filter(key => !['updated_at', 'created_at', 'ID_Usuario', 'IDUsuario', 'ID_CLIENTE'].includes(key))
                .map((key) => (
                  <div key={key} className="form-group">
                    <label>{CLIENT_LABEL_MAPPING[key] || key}:</label>
                    {key === 'ID' ? (
                      <span>{clienteEditado[key]}</span>
                    ) : key === 'Observacoes' || key === 'Observações' ? (
                      <textarea
                        value={clienteEditado[key] || ''}
                        onChange={(e) => setClienteEditado({ ...clienteEditado, [key]: e.target.value })}
                        style={{ resize: 'vertical' }}
                      />
                    ) : (
                      <input
                        type="text"
                        value={clienteEditado[key] || ''}
                        onChange={(e) => setClienteEditado({ ...clienteEditado, [key]: e.target.value })}
                      />
                    )}
                  </div>
                ))}
              <button className="btn-classic" onClick={salvarAlteracoes}>Salvar Alterações Cliente</button>

              <div className="navegacao">
                <button
                  className="btn-classic"
                  onClick={() => {
                    if (checkUnsavedChanges()) {
                      setClienteAtual(0);
                    }
                  }}
                  disabled={clienteAtual === 0}
                >
                  Primeiro
                </button>
                <button
                  className="btn-classic"
                  onClick={() => {
                    if (checkUnsavedChanges()) {
                      setClienteAtual((prev) => Math.max(prev - 1, 0));
                    }
                  }}
                  disabled={clienteAtual === 0}
                >
                  Anterior
                </button>
                <div className="nav-info">
                  <span className="nav-label">Cliente</span>
                  <span className="nav-count">{clienteAtual + 1} de {clientes.length}</span>
                </div>
                <button
                  className="btn-classic"
                  onClick={() => {
                    if (checkUnsavedChanges()) {
                      setClienteAtual((prev) => Math.min(prev + 1, clientes.length - 1));
                    }
                  }}
                  disabled={clienteAtual >= clientes.length - 1}
                >
                  Próximo
                </button>
                <button
                  className="btn-classic"
                  onClick={() => {
                    if (checkUnsavedChanges()) {
                      setClienteAtual(clientes.length - 1);
                    }
                  }}
                  disabled={clienteAtual >= clientes.length - 1}
                >
                  Último
                </button>
              </div>
            </div>
          ) : (
            <p>Nenhum cliente selecionado.</p>
          )}
        </main>

        <aside className="pedidos-section">
          <div className="pedidos-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3>Orçamentos do Cliente</h3>
            <button className="btn-classic" style={{ backgroundColor: '#4CAF50', color: 'white' }} onClick={() => openOrderModal()}>+ Novo Orçamento</button>
          </div>

          {loadingPedidos ? (
            <div className="loading-pedidos" style={{ textAlign: 'center', padding: '20px' }}>
              <img src="https://i.gifer.com/ZKZg.gif" alt="Buscando..." style={{ width: '40px' }} />
              <p>Buscando orçamentos...</p>
            </div>
          ) : pedidos.length > 0 ? (
            <ul className="pedidos-list" style={{ listStyle: 'none', padding: 0 }}>
              {pedidos.map((pedido, index) => (
                <li key={pedido.ID} className="pedido-item"
                  style={{ padding: '10px', border: '1px solid #ccc', marginBottom: '5px', cursor: 'pointer', backgroundColor: '#fff', borderRadius: '4px' }}
                  onClick={() => openOrderModal(pedido)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 'bold' }}>{index + 1} Orçamento #{pedido.ID} {pedido.OBS ? `- ${pedido.OBS.substring(0, 30)}${pedido.OBS.length > 30 ? '...' : ''}` : ''}</span>
                    <span>{new Date(pedido.DataPedido).toLocaleDateString('pt-BR')}</span>
                  </div>
                  <div style={{ color: '#007bff', fontWeight: 'bold' }}>
                    {formatCurrency(pedido.Frete)}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p>Nenhum orçamento encontrado.</p>
          )}
        </aside>
      </div>

      {isOrderModalOpen && (
        <OrderModal
          order={selectedOrder}
          clientId={clientes[clienteAtual]?.ID}
          client={clientes[clienteAtual]}
          onClose={() => setIsOrderModalOpen(false)}
          onSave={buscarPedidos}
        />
      )}

      {isClientSelectorOpen && (
        <ClientSelectorModal
          onClose={() => setIsClientSelectorOpen(false)}
          onSelect={(client) => {
            const index = clientes.findIndex(c => String(c.ID) === String(client.ID));
            if (index !== -1) {
              setClienteAtual(index);
            } else {
              // If not in current filtered list, add it and select
              setClientes([client]);
              setClienteAtual(0);
            }
            setIsClientSelectorOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default CadastroClientes;
